import { z } from "zod";

export const PROTOCOL_VERSION = 16;
export const PROTOCOL_SERVER_GENERATION = "2026-10-31.1";

export const ProtocolClientKind = z.enum(["web", "native", "worker"]);
export type ProtocolClientKind = z.infer<typeof ProtocolClientKind>;

export const ProtocolReady = z.object({
  type: z.literal("protocol.ready"),
  version: z.number().int().positive(),
  serverGeneration: z.string(),
  /** Present on organization sockets. Clients use it as the baseline for
   * detecting missed org.update frames after their snapshot reconciliation. */
  orgRevision: z.number().int().nonnegative().optional(),
});
export type ProtocolReady = z.infer<typeof ProtocolReady>;

export const ProtocolIncompatible = z.object({
  type: z.literal("protocol.incompatible"),
  code: z.literal("upgrade_required"),
  message: z.string(),
  expectedVersion: z.number().int().positive(),
  serverGeneration: z.string(),
});
export type ProtocolIncompatible = z.infer<typeof ProtocolIncompatible>;

export type ProtocolNegotiation = ProtocolReady | ProtocolIncompatible;

export function negotiateProtocolVersion(
  params: URLSearchParams,
  acceptedKinds: readonly ProtocolClientKind[] = ["web", "native", "worker"],
): ProtocolNegotiation {
  const version = Number(params.get("protocolVersion"));
  const kind = ProtocolClientKind.safeParse(params.get("clientKind"));
  if (!kind.success || !acceptedKinds.includes(kind.data)) {
    return incompatible("The client kind is missing or is not permitted on this socket endpoint.");
  }
  if (!Number.isInteger(version) || version !== PROTOCOL_VERSION) {
    return incompatible(`Rivets protocol ${PROTOCOL_VERSION} is required.`);
  }
  return { type: "protocol.ready", version, serverGeneration: PROTOCOL_SERVER_GENERATION };
}

export function withProtocolVersion(url: string, clientKind: ProtocolClientKind, clientVersion?: string): string {
  const value = new URL(url);
  value.searchParams.set("protocolVersion", String(PROTOCOL_VERSION));
  value.searchParams.set("clientKind", clientKind);
  if (clientVersion) value.searchParams.set("clientVersion", clientVersion);
  return value.toString();
}

function incompatible(message: string): ProtocolIncompatible {
  return {
    type: "protocol.incompatible",
    code: "upgrade_required",
    message,
    expectedVersion: PROTOCOL_VERSION,
    serverGeneration: PROTOCOL_SERVER_GENERATION,
  };
}
